import { useCallback, useEffect, useRef, useState } from "react";
import { Container } from "reactstrap";

import styles from "Pages/Admin/EditMatchTools/EditMatchTools.module.css";
import ReactTable from "components/ReactTable/ReactTable";
import EnabledFilter from "Pages/Admin/EditMatchTools/Components/EnabledFilter/EnabledFilter";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";
import useUserContext from "Hooks/useUserContext";
import usePageTitle from "Hooks/usePageTitle";

export default function EditMatchTools() {
    const [toolsData, setToolsData] = useState<ITool[]>();
    const [isLoading, setIsLoading] = useState(false);

    const { get, post } = useFetch();
    const { newAlert } = useAlertContext();
    const [, onSetUserContext] = useUserContext();

    usePageTitle("Edit Match Tools");
    const ignore = useRef(false);

    // Reflects the current company's tools live from the provider service (server-side),
    // so the list is always fresh — no manual "refresh from provider" step needed.
    const getTools = useCallback(async () => {
        setIsLoading(true);
        const response = await get(`/api/match_tools/for_current_company`);

        let data: ITool[] | Response;
        try {
            data = await response.json();
        } catch {
            data = response;
        }

        if (ignore.current) return;

        if (response.ok) {
            const tools = (data as ITool[]).sort((a, b) =>
                a.description.localeCompare(b.description)
            );
            setToolsData(tools);
            // Keep the nav menu in sync with the enabled tools.
            onSetUserContext({
                matchTools: tools.filter((t) => t.is_enabled),
            });
        } else {
            newAlert({ message: "Error loading match tools", color: "danger" });
        }

        setIsLoading(false);
    }, [get, newAlert, onSetUserContext]);

    useEffect(() => {
        ignore.current = false;

        getTools();

        return () => {
            ignore.current = true;
        };
    }, [getTools]);

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const id = e.target.name;
        const isChecked = e.target.checked;

        const response = await post(
            `/api/match_tools/toggle/${id}/${isChecked}`
        );

        if (response.ok) {
            const data = await response.json();

            const newToolsData = [...toolsData!].map((td) =>
                td.id === data.id ? { ...td, is_enabled: data.is_enabled } : td
            );
            setToolsData(newToolsData);

            // Keep the nav menu in sync with the enabled tools.
            onSetUserContext({
                matchTools: newToolsData.filter((t) => t.is_enabled),
            });
        } else {
            newAlert({ message: "Error updating tool", color: "danger" });
        }
    };

    const columns = [
        {
            Header: "Tool",
            accessor: "description",
        },
        {
            Header: "hidden",
            accessor: "method_name",
        },
        {
            Header: "Enabled",
            accessor: "is_enabled",
            width: 75,
            Cell: ({ row }: { row: any }) => (
                <label className={styles.switch}>
                    <input
                        type="checkbox"
                        name={row.original.id}
                        checked={row.values.is_enabled}
                        onChange={handleChange}
                    />
                    <span className={styles.slider}></span>
                </label>
            ),
            Filter: ({ column }: { column: any }) => (
                <EnabledFilter column={column} />
            ),
        },
    ];

    const tableProps = {
        data: toolsData,
        columns,
        showPagination: false,
        showExportCsv: false,
        isLoading,
    };

    return (
        <Container fluid>
            <h1>Edit Match Tools</h1>

            {toolsData && <ReactTable {...tableProps} />}
        </Container>
    );
}
